import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { useMatches } from 'react-router'

type SentryRouteHandle = { sentryTransactionPath?: string }

/**
 * Overrides Sentry's auto-derived transaction name with the deepest matched
 * route's `handle.sentryTransactionPath` on every navigation.
 *
 * `reactRouterV7BrowserTracingIntegration` derives names from matched route
 * paths, but URL-encoded segments and mid-mount `navigate({ replace: true })`
 * calls (e.g. `useCatalogRouteSync`) can leave Sentry with the raw
 * URL-encoded path instead of the parameterized one. Mounted once in
 * `RootLayout`, this hook applies the per-route handle as the authoritative
 * name so feature containers don't need their own override hooks.
 */
export const useRouteSentryTransaction = (): void => {
  const matches = useMatches()
  useEffect(() => {
    if (!import.meta.env.VITE_SENTRY_DSN) return
    for (let i = matches.length - 1; i >= 0; i--) {
      const path = (matches[i]!.handle as SentryRouteHandle | undefined)
        ?.sentryTransactionPath
      if (path) {
        Sentry.getCurrentScope().setTransactionName(path)
        // Also rename the active root span so consumers that read the span
        // description (e.g. `usePanelSentryTransaction`) see the parameterized
        // name and don't fall back to `'unknown'`.
        const activeSpan = Sentry.getActiveSpan()
        const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined
        rootSpan?.updateName(path)
        return
      }
    }
  }, [matches])
}
